import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building,
  Clock,
  DollarSign,
  User,
  Calendar,
} from "lucide-react";
import { formatCurrency, formatDate } from "@employee-management/common";
import type { AllReportData } from "@/types/reports.types";
import { useRouter } from "@tanstack/react-router";

interface AllReportsTableProps {
  data: AllReportData[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  onPageChange?: (page: number) => void;
  onSortChange?: (sortBy: string, sortOrder: "asc" | "desc") => void;
  currentSort?: { sortBy: string; sortOrder: "asc" | "desc" };
}

export function AllReportsTable({
  data,
  pagination,
  onPageChange,
  onSortChange,
  currentSort,
}: AllReportsTableProps) {
  const { buildLocation } = useRouter();

  const getSortIcon = (field: string) => {
    if (currentSort?.sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return currentSort.sortOrder === "asc" ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  const handleSort = (field: string) => {
    if (!onSortChange) return;
    const newOrder =
      currentSort?.sortBy === field && currentSort?.sortOrder === "asc"
        ? "desc"
        : "asc";
    onSortChange(field, newOrder);
  };

  const columns: ColumnDef<AllReportData>[] = [
    {
      accessorKey: "projectName",
      header: () => (
        <div className="w-[16ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleSort("projectName")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <Building className="h-4 w-4" />
            Project
            {getSortIcon("projectName")}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue("projectName")}</div>
      ),
    },
    {
      accessorKey: "employeeName",
      header: () => (
        <div className="w-[20ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleSort("employeeName")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <User className="h-4 w-4" />
            Employee
            {getSortIcon("employeeName")}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue("employeeName")}</div>
      ),
    },

    {
      accessorKey: "employeeDepartment",
      header: () => (
        <div className="w-[14ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleSort("employeeDepartment")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <Building className="h-4 w-4" />
            Department
            {getSortIcon("employeeDepartment")}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-muted-foreground">
          {row.getValue("employeeDepartment")}
        </div>
      ),
    },

    {
      accessorKey: "clientName",
      header: () => (
        <div className="w-[16ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleSort("clientName")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <Building className="h-4 w-4" />
            Client
            {getSortIcon("clientName")}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-muted-foreground">
          {row.getValue("clientName")}
        </div>
      ),
    },
    {
      accessorKey: "supervisorName",
      header: () => (
        <div className="w-[14ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleSort("supervisorName")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <User className="h-4 w-4" />
            Supervisor
            {getSortIcon("supervisorName")}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-muted-foreground">
          {row.getValue("supervisorName")}
        </div>
      ),
    },
    {
      accessorKey: "projectManagerName",
      header: () => (
        <div className="w-[16ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleSort("projectManagerName")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <User className="h-4 w-4" />
            Project Manager
            {getSortIcon("projectManagerName")}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-muted-foreground">
          {row.getValue("projectManagerName") || "-"}
        </div>
      ),
    },
    {
      accessorKey: "overtimeDate",
      header: () => (
        <div className="w-[10ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleSort("overtimeDate")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <Calendar className="h-4 w-4" />
            Date
            {getSortIcon("overtimeDate")}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-muted-foreground">
          {formatDate(new Date(row.getValue("overtimeDate")))}
        </div>
      ),
    },
    {
      accessorKey: "totalHours",
      header: () => (
        <div className="w-[8ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleSort("totalHours")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <Clock className="h-4 w-4" />
            Hours
            {getSortIcon("totalHours")}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-center">{row.getValue("totalHours")}h</div>
      ),
    },
    {
      accessorKey: "totalAmount",
      header: () => (
        <div className="w-[10ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleSort("totalAmount")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <DollarSign className="h-4 w-4" />
            Amount
            {getSortIcon("totalAmount")}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-center font-medium">
          {formatCurrency(row.getValue("totalAmount"))}
        </div>
      ),
    },
    {
      accessorKey: "paymentStatus",
      header: () => (
        <div className="w-[10ch]">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleSort("paymentStatus")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <DollarSign className="h-4 w-4" />
            Payment
            {getSortIcon("paymentStatus")}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-center">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              row.getValue("paymentStatus")
                ? "bg-green-100 text-green-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {row.getValue("paymentStatus") ? "Paid" : "Pending"}
          </span>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="!bg-muted/40">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="cursor-pointer"
                  onClick={() => {
                    const url = buildLocation({
                      to: "/projects/$projectId",
                      params: {
                        projectId: row.original.projectId,
                      },
                      search: {
                        tab: "billing",
                        entryId: row.original.entryId,
                      },
                    }).href;

                    if (url) {
                      window.open(url, "_blank", "noopener,noreferrer");
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No data to show
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="flex items-center justify-center md:justify-between gap-4 flex-wrap py-4">
          <div className="text-sm text-muted-foreground">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total} results
          </div>
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(pagination.page - 1)}
              disabled={!pagination.hasPrev}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(pagination.page + 1)}
              disabled={!pagination.hasNext}
            >
              Next
            </Button>
          </div>
        </div>

        // <div className="flex flex-col items-center justify-around gap-5 space-x-2 py-4 sm:flex sm:flex-row sm:justify-between">
        //   <div className="text-sm text-muted-foreground">
        //     Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
        //     {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
        //     {pagination.total} results
        //   </div>
        //   <div className="space-x-2 ">
        //     <Button
        //       variant="outline"
        //       size="sm"
        //       onClick={() => onPageChange?.(pagination.page - 1)}
        //       disabled={!pagination.hasPrev}
        //     >
        //       Previous
        //     </Button>
        //     <span className="text-sm text-muted-foreground">
        //       Page {pagination.page} of {pagination.totalPages}
        //     </span>
        //     <Button
        //       variant="outline"
        //       size="sm"
        //       onClick={() => onPageChange?.(pagination.page + 1)}
        //       disabled={!pagination.hasNext}
        //     >
        //       Next
        //     </Button>
        //   </div>
        // </div>
      )}
    </div>
  );
}
